import { useId, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/shared/cn';
import type { OreZoneMarker } from '@/versions/v1/schema/ore-zone';
import { vizToneColor, type VizTone } from './viz-tone';

export type OperatingCurvePoint = {
  x: number;
  y: number;
  label?: string;
  /** Ore zone on this production day — shape channel only (colour = judgement). */
  zone?: { id: string; label: string; marker: OreZoneMarker };
  /** Per-point tone override (curve judgement, MS-3 Q4) — falls back to the
   * curve's overall `tone` prop when absent. Point tone only; no new
   * geometry lives here. */
  tone?: VizTone;
};

type ZoneMarkerProps = {
  kind: OreZoneMarker;
  size?: number;
  fill?: string;
  fillOpacity?: number;
  className?: string;
};

/** Inline zone marker — same geometry as the chart trail marks. */
export function ZoneMarker({
  kind,
  size = 8,
  fill = 'currentColor',
  fillOpacity = 1,
  className,
}: ZoneMarkerProps) {
  const half = size / 2;
  if (kind === 'diamond') {
    const d = half * 1.15;
    return (
      <svg
        width={size}
        height={size}
        viewBox={`${-d} ${-d} ${d * 2} ${d * 2}`}
        className={cn('inline-block shrink-0', className)}
        aria-hidden
      >
        <polygon
          points={`0,${-d} ${d},0 0,${d} ${-d},0`}
          fill={fill}
          fillOpacity={fillOpacity}
        />
      </svg>
    );
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox={`${-half} ${-half} ${size} ${size}`}
      className={cn('inline-block shrink-0', className)}
      aria-hidden
    >
      <circle cx={0} cy={0} r={half} fill={fill} fillOpacity={fillOpacity} />
    </svg>
  );
}

export type OperatingCurveProps = {
  xLabel: string;
  yLabel: string;
  xUnit: string;
  yUnit: string;
  design: { x: number; y: number };
  frontier?: readonly OperatingCurvePoint[];
  trail: readonly OperatingCurvePoint[];
  current: OperatingCurvePoint;
  future?: OperatingCurvePoint & { label: string };
  tone: VizTone;
  height?: number;
  className?: string;
  caption?: string;
  cursorTooltip?: string;
  frontierProvenance?: string;
  /** Hover text for the provenance chip when `frontierProvenance` is a short
   *  label such as "[syn]"; defaults to the label itself. */
  frontierProvenanceTitle?: string;
  throughputMode?: boolean;
  /** The met 7-day operating record's own scale (polish.P26 r1): a tick at
   *  every whole point on the recovery axis, and units on the axis titles
   *  only — as the locked comp draws it. Default off; every other mount keeps
   *  the shared scale. */
  metRecoveryAxis?: boolean;
  xDomain: [number, number];
  yDomain: [number, number];
};

const PAD = { top: 12, right: 8, bottom: 28, left: 36 };
const MET_PAD = { top: 16, right: 12, bottom: 42, left: 44 };

function niceStep(rough: number): number {
  if (!Number.isFinite(rough) || rough <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const residual = rough / magnitude;
  const nice = residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 2.5 ? 2.5 : residual <= 5 ? 5 : 10;
  return nice * magnitude;
}

function formatTick(value: number, step: number): string {
  const decimals = Math.max(
    0,
    Math.ceil(-Math.log10(step)) + (step / 10 ** Math.floor(Math.log10(step)) % 1 ? 1 : 0),
  );
  return value.toFixed(Math.min(decimals, 3));
}

const CHAR_WIDTH = 6.2;
const MIN_TICK_GAP = 6;
const MAX_X_TICKS_GRIND = 4;
const MAX_Y_TICKS_GRIND = 3;
const MAX_X_TICKS_MET = 5;
const MAX_Y_TICKS_MET = 4;
/** The met record's unit-step recovery axis: six whole points fit without a
 *  crowding test ever thinning one out (the widest domain is Pb's 84–89). */
const MAX_Y_TICKS_MET_RECOVERY = 8;

function estimateTextWidth(text: string): number {
  return text.length * CHAR_WIDTH;
}

/** Place the grade/recovery `now` label clear of the design crosshair (its
 * full-height vertical and full-width horizontal), the trail marks and line,
 * and the plot edges. Candidates in order: preferred side, other side, above,
 * below; the first with no hit wins, else the one with the fewest hits. */
function clearNowLabel(args: {
  cx: number;
  cy: number;
  preferLeft: boolean;
  designX: number;
  designY: number;
  trail: readonly (readonly [number, number])[];
  plotLeft: number;
  plotRight: number;
  plotTop: number;
  plotBottom: number;
}): { x: number; y: number; textAnchor: 'start' | 'middle' | 'end' } {
  const { cx, cy, preferLeft, designX, designY, trail, plotLeft, plotRight, plotTop, plotBottom } = args;
  const w = estimateTextWidth('now');
  const left = { x: cx - 9, y: cy + 3, textAnchor: 'end' as const };
  const right = { x: cx + 9, y: cy + 3, textAnchor: 'start' as const };
  const candidates = [
    preferLeft ? left : right,
    preferLeft ? right : left,
    { x: cx, y: cy - 9, textAnchor: 'middle' as const },
    { x: cx, y: cy + 15, textAnchor: 'middle' as const },
  ];
  const hits = (c: (typeof candidates)[number]): number => {
    const l = c.textAnchor === 'end' ? c.x - w : c.textAnchor === 'middle' ? c.x - w / 2 : c.x;
    const r = l + w;
    const t = c.y - 8;
    const b = c.y + 2;
    let n = 0;
    if (l < plotLeft || r > plotRight || t < plotTop - 4 || b > plotBottom) n += 4;
    if (designX >= l - 3 && designX <= r + 3) n += 2;
    if (designY >= t - 2 && designY <= b + 2) n += 2;
    for (const [px, py] of trail) {
      if (px >= l - 4 && px <= r + 4 && py >= t - 4 && py <= b + 4) n += 1;
    }
    for (let i = 1; i < trail.length; i++) {
      const [x0, y0] = trail[i - 1]!;
      const [x1, y1] = trail[i]!;
      for (let x = Math.max(l, Math.min(x0, x1)); x <= Math.min(r, Math.max(x0, x1)); x += 2) {
        const y = x1 === x0 ? y0 : y0 + ((x - x0) / (x1 - x0)) * (y1 - y0);
        if (y >= t - 1 && y <= b + 1) {
          n += 1;
          break;
        }
      }
    }
    return n;
  };
  let best = candidates[0]!;
  let bestHits = Number.POSITIVE_INFINITY;
  for (const c of candidates) {
    const n = hits(c);
    if (n === 0) return c;
    if (n < bestHits) {
      best = c;
      bestHits = n;
    }
  }
  return best;
}

function tickBox(
  pos: number,
  text: string,
  anchor: 'start' | 'middle' | 'end',
): { left: number; right: number } {
  const width = estimateTextWidth(text);
  if (anchor === 'middle') {
    return { left: pos - width / 2, right: pos + width / 2 };
  }
  if (anchor === 'end') {
    return { left: pos - width, right: pos };
  }
  return { left: pos, right: pos + width };
}

function ticksOverlap(boxes: readonly { left: number; right: number }[]): boolean {
  for (let index = 1; index < boxes.length; index += 1) {
    if (boxes[index]!.left - boxes[index - 1]!.right < MIN_TICK_GAP) return true;
  }
  return false;
}

function ticksOverlapVertical(boxes: readonly { top: number; bottom: number }[]): boolean {
  for (let index = 1; index < boxes.length; index += 1) {
    if (boxes[index]!.top - boxes[index - 1]!.bottom < MIN_TICK_GAP) return true;
  }
  return false;
}

function buildAxisTicks(min: number, max: number, step: number): number[] {
  const ticks: number[] = [];
  for (let tick = min; tick <= max + step / 2; tick += step) {
    ticks.push(Number(tick.toFixed(6)));
  }
  return ticks;
}

function thinTicks(
  ticks: number[],
  maxCount: number,
  boxesFor: (candidate: number[]) => { left: number; right: number }[],
): number[] {
  let current = [...ticks];
  while (current.length > 1) {
    const boxes = boxesFor(current);
    if (current.length <= maxCount && !ticksOverlap(boxes)) break;
    if (current.length <= 2) break;
    const thinned: number[] = [current[0]!];
    for (let index = 1; index < current.length - 1; index += 1) {
      if (index % 2 === 1) thinned.push(current[index]!);
    }
    thinned.push(current[current.length - 1]!);
    if (thinned.length === current.length) {
      const mid = Math.floor(current.length / 2);
      current = current.filter((_, index) => index !== mid);
    } else {
      current = thinned;
    }
  }
  return current;
}

function thinVerticalTicks(
  ticks: number[],
  maxCount: number,
  yFor: (tick: number) => number,
): number[] {
  let current = [...ticks];
  while (current.length > 1) {
    const boxes = current.map((tick) => {
      const y = yFor(tick);
      return { top: y - 5, bottom: y + 5 };
    });
    if (current.length <= maxCount && !ticksOverlapVertical(boxes)) break;
    if (current.length <= 2) break;
    const thinned: number[] = [current[0]!];
    for (let index = 1; index < current.length - 1; index += 1) {
      if (index % 2 === 1) thinned.push(current[index]!);
    }
    thinned.push(current[current.length - 1]!);
    if (thinned.length === current.length) {
      const mid = Math.floor(current.length / 2);
      current = current.filter((_, index) => index !== mid);
    } else {
      current = thinned;
    }
  }
  return current;
}

function labelPlacement(
  anchorX: number,
  text: string,
  width: number,
  preferAnchor: 'start' | 'end',
  pad: typeof PAD,
): { x: number; textAnchor: 'start' | 'end' } {
  const plotLeft = pad.left;
  const plotRight = width - pad.right;
  const estWidth = text.length * 5.5;
  if (preferAnchor === 'start') {
    if (anchorX + estWidth > plotRight) {
      return { x: Math.min(plotRight - 2, anchorX), textAnchor: 'end' };
    }
    return { x: Math.max(plotLeft, anchorX), textAnchor: 'start' };
  }
  if (anchorX - estWidth < plotLeft) {
    return { x: Math.max(plotLeft + 2, anchorX), textAnchor: 'start' };
  }
  return { x: Math.min(plotRight, anchorX), textAnchor: 'end' };
}

export function OperatingCurve({
  xLabel,
  yLabel,
  xUnit,
  yUnit,
  design,
  frontier = [],
  trail,
  current,
  future,
  tone,
  height = 180,
  className,
  caption,
  cursorTooltip,
  frontierProvenance,
  frontierProvenanceTitle,
  throughputMode = false,
  metRecoveryAxis = false,
  xDomain,
  yDomain,
}: OperatingCurveProps) {
  const titleId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const descId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(280);

  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const measure = () => setWidth(Math.max(180, Math.round(el.getBoundingClientRect().width)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pad = throughputMode ? PAD : MET_PAD;
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const [paddedXMin, paddedXMax] = xDomain;
  const [paddedYMin, paddedYMax] = yDomain;
  const xRange = paddedXMax - paddedXMin || 1;
  const yRange = paddedYMax - paddedYMin || 1;
  const xTickStep = niceStep(xRange / 4);
  const yTickStep = metRecoveryAxis ? 1 : niceStep(yRange / (throughputMode ? 4 : 3));

  const toX = (value: number) => pad.left + ((value - paddedXMin) / xRange) * innerW;
  const toY = (value: number) => pad.top + innerH - ((value - paddedYMin) / yRange) * innerH;

  const baselineY = pad.top + innerH;
  const xTickY = throughputMode ? height - 6 : baselineY + 14;
  // Grade/recovery mode: the first x tick starts at the plot edge instead of
  // centring on it, so it clears the bottom y tick (`82 %`) in the corner.
  const xTickAnchor = (index: number, count: number): 'start' | 'middle' | 'end' =>
    index === count - 1 ? 'end' : index === 0 && !throughputMode ? 'start' : 'middle';
  const stroke = vizToneColor(tone);
  const plotRight = width - pad.right;

  const maxXTicks = throughputMode ? MAX_X_TICKS_GRIND : MAX_X_TICKS_MET;
  const maxYTicks = throughputMode
    ? MAX_Y_TICKS_GRIND
    : metRecoveryAxis
      ? MAX_Y_TICKS_MET_RECOVERY
      : MAX_Y_TICKS_MET;

  // Throughput mode ticks whole steps (74 · 75 · 76): starting at the domain's
  // own edge put "74" on 73.83, so each label sat off its value.
  const xTickCandidates = buildAxisTicks(
    throughputMode ? Math.ceil(paddedXMin / xTickStep - 1e-9) * xTickStep : paddedXMin,
    paddedXMax,
    xTickStep,
  );
  // The unit rides on the last tick label by default; the met record moves it
  // to the axis title alone, so it is not estimated in the thinning boxes.
  const xUnitOnTicks = Boolean(xUnit) && !metRecoveryAxis;
  const xTicks = thinTicks(xTickCandidates, maxXTicks, (candidate) =>
    candidate.map((tick, index) => {
      const isLast = index === candidate.length - 1;
      const label = formatTick(tick, xTickStep) + (isLast && xUnitOnTicks ? ` ${xUnit}` : '');
      const anchor = xTickAnchor(index, candidate.length);
      const pos = isLast ? Math.min(plotRight, toX(tick)) : toX(tick);
      return tickBox(pos, label, anchor);
    }),
  );

  const yTickCandidates = buildAxisTicks(paddedYMin, paddedYMax, yTickStep).reverse();
  // Throughput mode ticks the design line itself and the whole steps above it
  // (74 · 80 · 85 · 90 on the grind curve), stacked on one x, so they thin by
  // their vertical boxes. A step within 0.6 of the design line would crowd it.
  const yTicks = throughputMode
    ? thinVerticalTicks(
        [
          ...buildAxisTicks(
            Math.ceil(paddedYMin / yTickStep) * yTickStep,
            paddedYMax,
            yTickStep,
          ).filter((tick) => tick <= paddedYMax + 1e-6 && Math.abs(tick - design.y) >= yTickStep * 0.6),
          design.y,
        ].sort((a, b) => b - a),
        maxYTicks + 1,
        (tick) => toY(tick),
      )
    : thinVerticalTicks(yTickCandidates, maxYTicks, (tick) => toY(tick));

  const trailLine =
    trail.length > 1
      ? trail
          .map((pt, index) => {
            const x = toX(pt.x);
            const y = toY(pt.y);
            return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
          })
          .join(' ')
      : '';

  // Labels sit on the side away from the arrow: `now` opposite the future point, the
  // future label opposite `now`, so neither text crosses the dashed connector.
  const futureRightOfNow = future ? toX(future.x) >= toX(current.x) : false;
  const futureLabelPlacement = future
    ? futureRightOfNow
      ? labelPlacement(toX(future.x) + 7, future.label, width, 'start', pad)
      : labelPlacement(toX(future.x) - 7, future.label, width, 'end', pad)
    : null;

  // When the far side has no room the clamp would flip the label back across the
  // connector — drop it beneath the mark instead.
  const futureLabelBelow =
    !!futureLabelPlacement &&
    (futureRightOfNow
      ? futureLabelPlacement.textAnchor === 'end'
      : futureLabelPlacement.textAnchor === 'start');

  // `now` sits on the side clear of whatever crowds the point: the future
  // arrow when there is one (met mounts omit it), otherwise the most
  // recent trail step. A narrow domain (e.g. Ag's 11.1-12.1 oz/t) can put
  // the last trail dot almost on top of a right-anchored label, clipping
  // it (ruling #258 round 2, "the Now label"); checking the trail too —
  // not just the future point — keeps the text clear of both.
  const lastTrailPoint = trail[trail.length - 1];
  const trailRightOfNow = lastTrailPoint ? toX(lastTrailPoint.x) >= toX(current.x) : false;
  const nowCrowdedFromRight = future ? futureRightOfNow : trailRightOfNow;
  const designX = toX(design.x);
  const designY = toY(design.y);

  const nowSidePlacement = nowCrowdedFromRight
    ? labelPlacement(toX(current.x) - 9, 'now', width, 'end', pad)
    : labelPlacement(toX(current.x) + 9, 'now', width, 'start', pad);
  // Grade/recovery mode: a narrower plot can land `now` on the design
  // crosshair or across the trail. Try the side the trail test prefers, then
  // the other side, then above and below the point, and take the first spot
  // whose box clears the crosshair, the trail and the plot edge (fewest hits
  // otherwise). Throughput mode keeps the side test alone.
  const nowLabel: { x: number; y: number; textAnchor: 'start' | 'middle' | 'end' } = throughputMode
    ? { ...nowSidePlacement, y: toY(current.y) + 3 }
    : clearNowLabel({
        cx: toX(current.x),
        cy: toY(current.y),
        preferLeft: nowCrowdedFromRight,
        designX,
        designY,
        trail: trail.map((pt) => [toX(pt.x), toY(pt.y)] as const),
        plotLeft: pad.left,
        plotRight,
        plotTop: pad.top,
        plotBottom: baselineY,
      });
  const designLabelPlacement = labelPlacement(plotRight, 'design', width, 'end', pad);
  // Throughput mode names the point's own value and sits right of it (the
  // comp's spot); a halo lifts it off the faded record dots.
  const throughputNowText = `now · ${formatTick(current.y, 1)}${yUnit ? ` ${yUnit}` : ''}`;
  const throughputNowLabel = labelPlacement(toX(current.x) + 8, throughputNowText, width, 'start', pad);

  return (
    <div ref={wrapperRef} className={cn('w-full overflow-visible', className)}>
      {caption ? (
        <p className="mb-1 truncate text-[10px] text-muted">{caption}</p>
      ) : null}
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        className="block max-w-full"
        role="graphics-document"
        aria-labelledby={`${titleId} ${descId}`}
      >
        <title id={titleId}>
          {xLabel} vs {yLabel}
        </title>
        <desc id={descId}>
          Operating curve with {trail.length}-point trail and current point
        </desc>

        {!throughputMode ? (
          <defs>
            <marker
              id={`${titleId}-arrowhead`}
              markerWidth="4"
              markerHeight="4"
              refX="3"
              refY="2"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <polygon points="0,0 4,2 0,4" fill={stroke} opacity={0.8} />
            </marker>
          </defs>
        ) : null}

        {yTicks.map((tick, index) => {
          const y = toY(tick);
          const isLast = index === yTicks.length - 1;
          return (
            <g key={`y-${tick}`}>
              <line
                x1={pad.left}
                x2={width - pad.right}
                y1={y}
                y2={y}
                stroke="var(--hairline)"
                strokeWidth={1}
              />
              <text
                x={pad.left - 6}
                y={y + 3}
                textAnchor="end"
                className="fill-[var(--ink-tertiary)] text-[10px] tabular-nums"
              >
                {formatTick(tick, yTickStep)}
                {isLast && yUnit && !throughputMode && !metRecoveryAxis ? ` ${yUnit}` : ''}
              </text>
            </g>
          );
        })}

        {/* Grade/recovery mode: x ticks sit just under the plot, clear of the
            axis title on the bottom line (at h − 14 they overlapped it). */}
        {xTicks.map((tick, index) => {
          const isLast = index === xTicks.length - 1;
          const xPos = toX(tick);
          const tickLabel =
            formatTick(tick, xTickStep) + (isLast && xUnitOnTicks ? ` ${xUnit}` : '');
          const anchor = xTickAnchor(index, xTicks.length);
          const labelX = isLast ? Math.min(plotRight, xPos) : anchor === 'start' ? xPos + 2 : xPos;
          return (
            <text
              key={`x-${tick}`}
              x={labelX}
              y={xTickY}
              textAnchor={anchor}
              className="fill-[var(--ink-tertiary)] text-[10px] tabular-nums"
            >
              {tickLabel}
            </text>
          );
        })}

        {!throughputMode ? (
          <>
            <text
              x={(pad.left + plotRight) / 2}
              y={height - 4}
              textAnchor="middle"
              className="fill-[var(--ink-tertiary)] text-xs font-medium uppercase tracking-wide"
            >
              {xLabel} ({xUnit})
            </text>
            <text
              x={9}
              y={(pad.top + baselineY) / 2}
              textAnchor="middle"
              transform={`rotate(-90 9 ${(pad.top + baselineY) / 2})`}
              className="fill-[var(--ink-tertiary)] text-xs font-medium uppercase tracking-wide"
            >
              {yLabel} ({yUnit})
            </text>
          </>
        ) : null}

        {throughputMode ? (
          <g>
            <line
              x1={pad.left}
              x2={width - pad.right}
              y1={toY(design.y)}
              y2={toY(design.y)}
              stroke="var(--ink-tertiary)"
              strokeWidth={1}
              strokeDasharray="4 3"
            />
            <text
              x={plotRight}
              y={toY(design.y) - 4}
              textAnchor="end"
              className="fill-[var(--ink-tertiary)] text-[10px]"
            >
              Design P80 line
            </text>
          </g>
        ) : (
          <g>
            <line
              x1={designX}
              x2={designX}
              y1={pad.top}
              y2={baselineY}
              stroke="var(--ink-tertiary)"
              strokeWidth={1}
              strokeDasharray="4 3"
            />
            <line
              x1={pad.left}
              x2={plotRight}
              y1={designY}
              y2={designY}
              stroke="var(--ink-tertiary)"
              strokeWidth={1}
              strokeDasharray="4 3"
            />
            <circle
              cx={designX}
              cy={designY}
              r={4.5}
              fill="var(--surface-2)"
              stroke="var(--ink-primary)"
              strokeWidth={1.5}
            />
            <text
              x={designLabelPlacement.x}
              y={designY - 5}
              textAnchor={designLabelPlacement.textAnchor}
              className="fill-[var(--ink-tertiary)] text-[10px]"
            >
              design
            </text>
          </g>
        )}

        {throughputMode ? (
          <defs>
            <clipPath id={`${titleId}-plot`}>
              <rect x={pad.left} y={pad.top} width={innerW} height={innerH} />
            </clipPath>
          </defs>
        ) : null}
        {frontier.length > 0 ? (
          <path
            clipPath={throughputMode ? `url(#${titleId}-plot)` : undefined}
            d={frontier
              .map((pt, index) => {
                const x = toX(pt.x);
                const y = toY(pt.y);
                return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
              })
              .join(' ')}
            fill="none"
            stroke="var(--ink-tertiary)"
            strokeWidth={1.5}
            strokeDasharray="5 4"
          />
        ) : null}

        {/* Throughput mode draws the 7-day record as dots faded by age: joined
            in record order the line crossed itself into a knot. */}
        {trailLine && !throughputMode ? (
          <path
            d={trailLine}
            fill="none"
            stroke={stroke}
            strokeWidth={throughputMode ? 1.5 : 1.25}
            strokeLinecap="round"
            strokeOpacity={throughputMode ? 1 : 0.55}
          />
        ) : null}

        {trail.map((pt, index) => {
          if (throughputMode) {
            const opacity = 0.15 + (0.45 * (index + 1)) / Math.max(trail.length, 1);
            return (
              <circle
                key={`trail-${index}`}
                cx={toX(pt.x)}
                cy={toY(pt.y)}
                r={2.5}
                fill={stroke}
                fillOpacity={opacity}
                stroke="var(--surface-2)"
                strokeWidth={1}
              >
                {pt.label ? <title>{pt.label}</title> : null}
              </circle>
            );
          }
          const isOffCurve = pt.tone === 'bad';
          const recency = trail.length > 1 ? index / (trail.length - 1) : 1;
          // Sized and filled to read on both paper and dark glass; the halo
          // lifts each mark off the trail line.
          const radius = 2 + 1.5 * recency;
          const cx = toX(pt.x);
          const cy = toY(pt.y);
          const fill = pt.tone ? vizToneColor(pt.tone) : stroke;
          const fillOpacity = isOffCurve ? 1 : 0.8;
          const marker = pt.zone?.marker ?? 'circle';
          if (marker === 'diamond') {
            const d = radius * 1.15;
            return (
              <polygon
                key={`trail-${index}`}
                points={`${cx},${cy - d} ${cx + d},${cy} ${cx},${cy + d} ${cx - d},${cy}`}
                fill={fill}
                fillOpacity={fillOpacity}
                stroke="var(--surface-2)"
                strokeWidth={0.75}
              >
                {pt.label ? <title>{pt.label}</title> : null}
              </polygon>
            );
          }
          return (
            <circle
              key={`trail-${index}`}
              cx={cx}
              cy={cy}
              r={radius}
              fill={fill}
              fillOpacity={fillOpacity}
              stroke="var(--surface-2)"
              strokeWidth={0.75}
            >
              {pt.label ? <title>{pt.label}</title> : null}
            </circle>
          );
        })}

        {throughputMode ? (
          <circle
            cx={toX(design.x)}
            cy={toY(design.y)}
            r={3}
            fill="var(--surface-2)"
            stroke="var(--ink-tertiary)"
            strokeWidth={1.5}
          >
            <title>Design point</title>
          </circle>
        ) : null}
        {throughputMode ? (
          <text
            data-curve-design-label
            x={toX(design.x) + 6}
            y={toY(design.y) + 13}
            className="fill-[var(--ink-secondary)] text-[10px] tabular-nums"
          >
            design {formatTick(design.x, xTickStep)} · {formatTick(design.y, yTickStep)}
          </text>
        ) : null}

        {throughputMode ? (
          <circle
            cx={toX(current.x)}
            cy={toY(current.y)}
            r={4}
            fill={stroke}
            stroke="var(--surface-2)"
            strokeWidth={1.5}
          >
            <title>{cursorTooltip ?? current.label ?? 'Current'}</title>
          </circle>
        ) : null}
        {throughputMode ? (
          <text
            data-curve-now-label
            x={throughputNowLabel.x}
            y={toY(current.y) + 3}
            textAnchor={throughputNowLabel.textAnchor}
            paintOrder="stroke"
            stroke="var(--surface-2)"
            strokeWidth={3}
            className="fill-[var(--ink-primary)] text-[10px] font-medium tabular-nums"
          >
            {throughputNowText}
          </text>
        ) : (
          <>
            <circle
              cx={toX(current.x)}
              cy={toY(current.y)}
              r={5}
              fill={current.tone ? vizToneColor(current.tone) : stroke}
              stroke="var(--surface-2)"
              strokeWidth={1.5}
            >
              <title>{cursorTooltip ?? current.label ?? 'Current'}</title>
            </circle>
            <text
              x={nowLabel.x}
              y={nowLabel.y}
              textAnchor={nowLabel.textAnchor}
              className="fill-[var(--ink-secondary)] text-[10px]"
            >
              now
            </text>
          </>
        )}

        {future ? (
          throughputMode ? (
            <g>
              <line
                x1={toX(current.x)}
                y1={toY(current.y)}
                x2={toX(future.x)}
                y2={toY(future.y)}
                stroke={stroke}
                strokeWidth={1}
                strokeDasharray="2 3"
                opacity={0.7}
              />
              <circle
                cx={toX(future.x)}
                cy={toY(future.y)}
                r={4}
                fill="var(--surface-2)"
                stroke={stroke}
                strokeWidth={1.5}
              >
                <title>{future.label}</title>
              </circle>
              <text
                x={futureLabelBelow ? toX(future.x) : futureLabelPlacement!.x}
                y={futureLabelBelow ? toY(future.y) + 14 : toY(future.y) + 3}
                textAnchor={futureLabelBelow ? 'middle' : futureLabelPlacement!.textAnchor}
                className="fill-[var(--ink-secondary)] text-[10px]"
              >
                {future.label}
              </text>
            </g>
          ) : (
            <g>
              <line
                x1={toX(current.x)}
                y1={toY(current.y)}
                x2={toX(future.x)}
                y2={toY(future.y)}
                stroke={stroke}
                strokeWidth={1}
                strokeDasharray="3 3"
                opacity={0.8}
                markerEnd={`url(#${titleId}-arrowhead)`}
              />
              <polygon
                points={`${toX(future.x)},${toY(future.y) - 3.5} ${toX(future.x) + 3.5},${toY(future.y)} ${toX(future.x)},${toY(future.y) + 3.5} ${toX(future.x) - 3.5},${toY(future.y)}`}
                fill="var(--surface-2)"
                stroke={stroke}
                strokeWidth={1.5}
              >
                <title>{future.label}</title>
              </polygon>
              <text
                x={futureLabelBelow ? toX(future.x) : futureLabelPlacement!.x}
                y={futureLabelBelow ? toY(future.y) + 14 : toY(future.y) + 3}
                textAnchor={futureLabelBelow ? 'middle' : futureLabelPlacement!.textAnchor}
                className="fill-[var(--ink-secondary)] text-[10px]"
              >
                {future.label}
              </text>
            </g>
          )
        ) : null}
      </svg>
      {throughputMode ? (
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 overflow-visible">
          <p className="text-[10px] text-muted">
            {xLabel} · {yLabel}
          </p>
          {frontierProvenance ? (
            <div className="overflow-visible">
              {/* Throughput mode sits on the Plant model, whose one [syn] mark is
                  the neutral dashed chip (polish.P11 r1). */}
              <span
                title={frontierProvenanceTitle ?? frontierProvenance}
                data-syn-chip
                className="inline-flex items-center rounded-full border border-dashed border-[var(--hairline-strong)] px-1.5 font-mono text-[10px] leading-4 text-muted"
              >
                {frontierProvenance}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
